import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Archive, Trash2, Loader2 } from "lucide-react";

interface ChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string;
  hasDisabledField: boolean;
  onArchive: () => void;
  onPermanentDelete: () => void;
  isArchiving: boolean;
}

/**
 * Shown when a document has zero linked transactions — user can choose
 * between archive (safe, reversible) and permanent delete (irreversible).
 *
 * If the doctype has no disabled field, only permanent delete is shown.
 * Archive option uses primary styling (safe). Delete uses destructive styling.
 */
export function ChoiceDialog({
  open,
  onOpenChange,
  itemName,
  hasDisabledField,
  onArchive,
  onPermanentDelete,
  isArchiving,
}: ChoiceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Remove &ldquo;{itemName}&rdquo;?
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            No orders or transactions are linked to this item.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-2">
          {hasDisabledField && (
            <button
              type="button"
              onClick={onArchive}
              disabled={isArchiving}
              className="flex items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
            >
              {isArchiving ? (
                <Loader2 className="size-5 mt-0.5 shrink-0 animate-spin text-foreground" />
              ) : (
                <Archive className="size-5 mt-0.5 shrink-0 text-foreground" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground">Archive</div>
                <div className="text-sm text-muted-foreground">
                  Hide from lists. Restore anytime.
                </div>
              </div>
            </button>
          )}

          <button
            type="button"
            onClick={onPermanentDelete}
            disabled={isArchiving}
            className="flex items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            <Trash2 className="size-5 mt-0.5 shrink-0 text-destructive" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-destructive">
                Delete permanently
              </div>
              <div className="text-sm text-muted-foreground">
                This cannot be undone.
              </div>
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isArchiving}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
